import { useMemo, useRef, useState } from "react";
import { AlignLeft, CalendarDays, Clock, MapPin, X } from "lucide-react";
import { useCreateEvent } from "../../api/hooks";
import { KINDS, draftToInput, withStart, type Draft } from "../../lib/draft";
import { addDays } from "../../lib/date";
import { useUi, type Anchor } from "../../state/ui";
import { Popover } from "../ui/Popover";
import { DateSelect } from "../ui/DateSelect";
import { TimeSelect } from "../ui/TimeSelect";
import "./overlays.css";

/** Quick-create card, next to the clicked/dragged slot. */
export function QuickCreate({ draft: initial, anchor }: { draft: Draft; anchor: Anchor }) {
  const ui = useUi();
  const create = useCreateEvent();
  const [draft, setDraftState] = useState(initial);
  const requestId = useMemo(() => crypto.randomUUID(), []);
  const titleRef = useRef<HTMLInputElement>(null);
  const settings = ui.me.settings;

  // The grid shows the pending slot from the overlay's draft: keep it in sync.
  const setDraft = (d: Draft) => {
    setDraftState(d);
    ui.setOverlay({ kind: "quick", draft: d, anchor });
  };

  const save = () => {
    create.mutate(
      { ...draftToInput(draft), clientRequestId: requestId },
      {
        onSuccess: () => ui.showToast("Evento salvo"),
        onError: (err) => ui.showToast(`Não foi possível salvar: ${(err as Error).message}`)
      }
    );
    ui.setOverlay(null);
  };

  return (
    <Popover anchor={anchor} onClose={() => ui.setOverlay(null)} width={448} className="quick" keepOpen>
      <div className="quick-top">
        <button className="icon-btn sm" aria-label="Fechar" onClick={() => ui.setOverlay(null)}>
          <X size={18} />
        </button>
      </div>
      <form
        className="quick-body"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <input
          ref={titleRef}
          data-autofocus
          className="field quick-title"
          placeholder="Adicionar título"
          aria-label="Título"
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        />
        <div className="kind-tabs" role="tablist">
          {KINDS.map(({ kind: k, label }) => (
            <button key={k} type="button" role="tab" aria-selected={draft.kind === k} className={`kind-tab${draft.kind === k ? " active" : ""}`} onClick={() => setDraft({ ...draft, kind: k })}>
              {label}
            </button>
          ))}
        </div>

        <div className="quick-row">
          <Clock size={20} className="details-icon" />
          <div className="when-edit">
            <DateSelect value={draft.startDate} today={ui.today} weekStart={settings.weekStart} ariaLabel="Data" onChange={(d) => setDraft(withStart(draft, d, draft.startMin))} />
            {!draft.allDay && (
              <>
                <TimeSelect ariaLabel="Início" value={draft.startMin} onChange={(m) => setDraft(withStart(draft, draft.startDate, m))} />
                <span>–</span>
                <TimeSelect
                  ariaLabel="Término"
                  value={draft.endMin + (draft.endDate > draft.startDate ? 1440 : 0)}
                  startMin={draft.startMin}
                  onChange={(m) => setDraft({ ...draft, endDate: m >= 1440 ? addDays(draft.startDate, 1) : draft.startDate, endMin: m % 1440 })}
                />
              </>
            )}
            <label className="checkbox" style={{ marginLeft: 4 }}>
              <input
                type="checkbox"
                checked={draft.allDay}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    allDay: e.target.checked,
                    endDate: draft.startDate,
                    reminders: e.target.checked ? settings.defaultAllDayReminders : settings.defaultReminders
                  })
                }
              />
              Dia inteiro
            </label>
          </div>
        </div>

        <div className="quick-row">
          <MapPin size={20} className="details-icon" />
          <input className="field filled" placeholder="Adicionar local" value={draft.location} onChange={(e) => setDraft({ ...draft, location: e.target.value })} />
        </div>
        <div className="quick-row">
          <AlignLeft size={20} className="details-icon" />
          <input className="field filled" placeholder="Adicionar descrição" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
        </div>
        <div className="quick-row">
          <CalendarDays size={20} className="details-icon" />
          <select className="chip-select" value={draft.calendarId} aria-label="Agenda" onChange={(e) => setDraft({ ...draft, calendarId: e.target.value })}>
            {ui.calendars.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="quick-actions">
          <button type="button" className="btn text" onClick={() => ui.setOverlay({ kind: "editor", draft })}>
            Mais opções
          </button>
          <button type="submit" className="btn primary">
            Salvar
          </button>
        </div>
      </form>
    </Popover>
  );
}
